import React, { useEffect, useRef } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import WaveSelector from "./WaveSelector";
import "./LongitudinalChart.css";
import {
  adjustColorOpacity,
  baseColors,
  constructCustomLegends,
  createProjectHeading,
} from "../../constants/constant";
import { clarityNav } from "../../services/api/clarityNav/apiService";
import { useLocation } from "react-router-dom";
import emptyStateIcon from "../../assets/images/empty-chart.svg";
import { cleanDigitSectionValue } from "@mui/x-date-pickers/internals/hooks/useField/useField.utils";
import CustomLegends from "./CustomLegends/CustomLegends";
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting";

const LongitudinalChart = ({
  project_id,
  selectedPromptId,
  selectedPrompt,
}) => {
  const location = useLocation();
  const [selectedOptions, setSelectedOptions] = React.useState({});
  const [waveData, setWaveData] = React.useState(null);
  const [chartData, setChartData] = React.useState(null);
  const [showChart, setShowChart] = React.useState(false);
  const [custom_legends, setCustomLegends] = React.useState(null);
  const chartRef = useRef(null);
  const [getLongitudinalWaveData] =
    clarityNav.useGetLongitudinalWaveDataMutation();
  const [getLongitudinalData] = clarityNav.useGetLongitudinalDataMutation();

  useEffect(() => {
    const fetchWaveData = async () => {
      try {
        const response = await getLongitudinalWaveData({
          project_id,
          params: {
            prompt_id: selectedPromptId,
          },
        });
        if (response.data?.result?.data) {
          setWaveData(response.data.result.data);
        }
      } catch (error) {
        console.error("Error fetching wave data:", error);
      }
    };

    fetchWaveData();
  }, [getLongitudinalWaveData, project_id, selectedPromptId]);

  const handleOptionChange = (waveIndex, optionIndex, requestId) => {
    const newSelectedOptions = { ...selectedOptions };
    if (optionIndex === null) {
      // Remove the selection for this wave when deselected
      delete newSelectedOptions[waveIndex];
    } else {
      // Add new selection
      newSelectedOptions[waveIndex] = {
        optionIndex,
        requestId,
      };
    }
    setSelectedOptions(newSelectedOptions);
  };
  console.log(waveData, "waveData");

  const handleApplySelections = async () => {
    try {
      // Transform selectedOptions into the required payload format using wave data
      const selection = Object.entries(selectedOptions).map(
        ([waveIndex, data]) => {
          // Get the actual wave key (W6, W10 etc) from waveData
          const waveKey = Object.keys(waveData)[parseInt(waveIndex)];
          return {
            wave: waveKey, // Use the actual wave value from waveData
            request_id: data.requestId,
          };
        }
      );

      console.log("Selection payload:", selection);

      // If selection is empty, return early and do not call the API
      if (selection.length === 0) {
        return;
      }
      const payload = {
        prompt_id: selectedPromptId,
        selection,
      };

      const response = await getLongitudinalData({
        projectId: project_id,
        data: payload,
      });

      if (response.data) {
        setChartData(response?.data);
        setShowChart(true);
      }
    } catch (error) {
      console.error("Error applying selections:", error);
    }
  };
  console.log(chartData);

  useEffect(() => {
    if (!chartData || !showChart) return;

    const chartElement = document.getElementById("chartdiv");
    if (!chartElement) return;

    // Create root element
    const root = am5.Root.new("chartdiv");
    root._logo.dispose();

    // Set themes
    root.setThemes([am5themes_Animated.new(root)]);

    // Add export menu
    am5plugins_exporting.Exporting.new(root, {
      menu: am5plugins_exporting.ExportingMenu.new(root, {}),
    });

    // Create chart
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: "none",
        wheelY: "none",
        layout: root.verticalLayout,
      })
    );
    // Process the data first
    const processChartData = (responseData) => {
      if (!responseData?.result?.length) return [];

      return responseData.result.map((waveResult) => {
        const waveData = waveResult.genai_analysis_relation;

        const originalWave = waveResult.wave;
        const formattedWave = originalWave.replace(/^W/, "Wave ");

        const dataPoint = {
          wave: formattedWave, // used for display (x-axis)
          originalWave: originalWave, // used for internal keys (e.g., color mapping)
        };

        Object.entries(waveData.Primary_Themes).forEach(([key, theme]) => {
          dataPoint[theme] = waveData.Count[key] || 0;
        });

        return dataPoint;
      });
    };

    // Update the color mapping function to include themes from all waves
    const createThemeColorMapping = (responseData) => {
      const themeColorMapping = {};
      const waveThemeMapping = {};
      const siThemeColors = {}; // New object to track colors by SI and theme

      // First pass: Group themes by SI for each wave
      responseData.result.forEach((waveResult) => {
        const wave = waveResult.wave;
        const { SI, Primary_Themes } = waveResult.genai_analysis_relation;

        if (!waveThemeMapping[wave]) {
          waveThemeMapping[wave] = {};
        }

        // Group themes by SI value for this wave
        Object.entries(Primary_Themes).forEach(([key, theme]) => {
          const siValue = SI[key];
          if (!waveThemeMapping[wave][siValue]) {
            waveThemeMapping[wave][siValue] = [];
          }
          waveThemeMapping[wave][siValue].push(theme);

          // Track unique themes under each SI
          if (!siThemeColors[siValue]) {
            siThemeColors[siValue] = new Map(); // Use Map to track theme-color pairs
          }
          if (!siThemeColors[siValue].has(theme)) {
            siThemeColors[siValue].set(theme, null); // Initially set to null
          }
        });
      });

      // Second pass: Assign colors to themes within each SI
      Object.entries(siThemeColors).forEach(([si, themes], siIndex) => {
        const baseColor = baseColors[siIndex % baseColors.length];
        const themeArray = Array.from(themes.keys());

        themeArray.forEach((theme, themeIndex) => {
          const color = adjustColorOpacity(
            baseColor,
            themeIndex,
            themeArray.length
          );
          siThemeColors[si].set(theme, color);
        });
      });

      // Final pass: Map colors to wave-theme combinations
      Object.entries(waveThemeMapping).forEach(([wave, siGroups]) => {
        Object.entries(siGroups).forEach(([si, themes]) => {
          themes.forEach((theme) => {
            const color = siThemeColors[si].get(theme);
            themeColorMapping[`${wave}_${theme}`] = color;
          });
        });
      });

      return { themeColorMapping, waveThemeMapping };
    };

    // Process data and create color mapping
    const wave_theme_color_mapping = createThemeColorMapping(chartData);
    ///primary themes with color mapping respective to wave and si value

    /////>>>>>>>>>>>>>
    const custom_legends = constructCustomLegends(
      wave_theme_color_mapping?.waveThemeMapping,
      wave_theme_color_mapping?.themeColorMapping
    );
    setCustomLegends(custom_legends);
    console.log(custom_legends);
    /////>>>>>>>>>>>>>

    const data = processChartData(chartData);
    console.log(data, "data");

    // Process data before creating axes
    // Find the maximum total count across all waves
    const maxTotalCount = Math.max(
      ...data.map((wave) =>
        Object.entries(wave)
          .filter(([key]) => key !== "wave")
          .reduce((sum, [_, count]) => sum + count, 0)
      )
    );
    const yAxisMax = Math.ceil(maxTotalCount * 1.1); // Add 10% margin and round up
    console.log(yAxisMax, "yAxisMax");
    console.log(
      "Processing data for Y-axis with max total count:",
      maxTotalCount
    );

    // Create axes
    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "wave",
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 30,
          cellStartLocation: 0.2,
          cellEndLocation: 0.8,
        }),
        tooltip: am5.Tooltip.new(root, {}),
      })
    );

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        max: yAxisMax,
        renderer: am5xy.AxisRendererY.new(root, {
          minGridDistance: 30,
        }),
        numberFormat: "#",
        strictMinMax: true,
      })
    );

    yAxis.children.moveValue(
      am5.Label.new(root, {
        text: "Count",
        rotation: -90,
        y: am5.p50,
        centerX: am5.p50,
        fontWeight: "bold"
      }),
      0
    );

    // Instead of taking themes from only the first wave, compute the union of all themes across waves
    const uniqueThemes = Array.from(
      new Set(
        data.flatMap((wave) =>
          Object.keys(wave).filter((key) => key !== "wave")
        )
      )
    );

    // Create series for each unique theme
    uniqueThemes.forEach((theme) => {
      // Get the first wave's color for initial series color as fallback
      const firstWave = data[0].wave;
      const initialColor =
        wave_theme_color_mapping?.themeColorMapping[`${firstWave}_${theme}`] ||
        baseColors[0];

      const series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: theme,
          stacked: true,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: theme,
          categoryXField: "wave",
          fill: am5.color(initialColor),
          tooltip: am5.Tooltip.new(root, {
            getFillFromSprite: true,
            getStrokeFromSprite: true,
            autoTextColor: true,
            pointerOrientation: "horizontal",
            dy: -5
          }),
        })
      );

      //  percentage field is
      series.columns.template.adapters.add("tooltipText", () => {
        return `[bold]{name}[/]\nCount: [bold]{valueY}[/] ({${theme}_percent}%)`;
      });

      // Update the column template to use wave-specific colors
      series.columns.template.adapters.add("fill", (fill, target) => {
        const dataItem = target.dataItem;
        // if (dataItem) {
        //     const wave = dataItem.get("categoryX");
        //     return am5.color(wave_theme_color_mapping?.themeColorMapping[`${wave}_${theme}`] || baseColors[0]);
        // }
        if (dataItem) {
          const categoryX = dataItem.get("categoryX"); // This is "Wave1"
          const originalData = data.find((d) => d.wave === categoryX);
          const originalWave = originalData?.originalWave || categoryX;

          return am5.color(
            wave_theme_color_mapping?.themeColorMapping[
              `${originalWave}_${theme}`
            ] || baseColors[0]
          );
        }
        return fill;
      });

      series.columns.template.setAll({
        fillOpacity: 0.8,
        strokeWidth: 0,
        tooltipY: am5.percent(50),
        width: am5.percent(50),
        cursorOverStyle: "pointer",
        states: {
          hover: {
            fillOpacity: 1,
            strokeWidth: 2,
          },
        },
        tooltipPosition: "pointer",
      });

      // Ensure each data point has a value for the current theme (default to 0 if missing)
      // const seriesData = data.map(wave => ({
      //     ...wave,
      //     [theme]: wave[theme] || 0
      // }));

      const seriesData = data.map((wave) => {
        const total = Object.entries(wave)
          .filter(([key]) => key !== "wave" && key !== "originalWave")
          .reduce((sum, [_, count]) => sum + count, 0);

        const count = wave[theme] || 0;
        const percent = total > 0 ? (count / total) * 100 : 0;

        return {
          ...wave,
          [theme]: count,
          [`${theme}_percent`]: percent.toFixed(1), // e.g., "24.6"
        };
      });

      series.data.setAll(seriesData);
    });

    // Update legend configuration
    // const legend = chart.children.push(
    //     am5.Legend.new(root, {
    //         centerX: am5.p50,
    //         x: am5.p50,
    //         marginTop: 15,
    //         marginBottom: 15
    //     })
    // );

    // legend.markers.template.setAll({
    //     width: 18,
    //     height: 18,
    //     fillField: "fill",
    //     strokeField: "stroke"
    // });

    // legend.labels.template.setAll({
    //     fontSize: 12,
    //     fontWeight: "500",
    //     fill: am5.color(0x000000)
    // });

    // legend.data.setAll(chart.series.values);

    // Set data to x-axis
    xAxis.data.setAll(data);
    console.log(data, "xAxis");

    // Add total count labels at the top of stacked columns
    chart.series.values[chart.series.values.length - 1].bullets.push(() => {
      return am5.Bullet.new(root, {
        locationY: 1,
        sprite: am5.Label.new(root, {
          text: "{valueSum}",
          centerX: am5.p50,
          centerY: am5.p0,
          populateText: true,
          fontSize: 12,
          fill: am5.color(0x000000),
        }),
      });
    });

    // Configure y-axis grid and labels
    yAxis.get("renderer").grid.template.setAll({
      strokeWidth: 1,
      stroke: am5.color(0xcccccc),
    });

    // Update the y-axis settings to use the new max value
    yAxis.setAll({
      strictMinMax: true,
      maxPrecision: 0,
      numberFormat: "#",
      min: 0,
      max: yAxisMax,
      step: 1,
    });

    // Add title
    chart.children.unshift(
      am5.Label.new(root, {
        fontSize: 18,
        fontWeight: "500",
        textAlign: "center",
        x: am5.percent(50),
        centerX: am5.percent(50),
        paddingTop: 0,
        paddingBottom: 20,
      })
    );

    // Animate series appearance
    chart.series.values.forEach((series) => {
      series.appear(1000, 100);
    });

    // Add cursor for better interaction
    chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "none",
        xAxis: xAxis,
        yAxis: yAxis,
        snapToSeries: chart.series.values,
      })
    );

    return () => {
      root.dispose();
    };
  }, [chartData, showChart]);
  console.log(custom_legends, "custom_legends");

  return (
    <div className="longitudinal-view">
      <div className="left-panel">
        <WaveSelector
          waveData={waveData}
          selectedOptions={selectedOptions}
          onOptionChange={handleOptionChange}
          onApplySelections={handleApplySelections}
          selectedPrompt={selectedPrompt}
        />
      </div>
      <div className="right-panel">
        <div className="chart-header">
          <span className="project-title-text">
            {createProjectHeading.MVOC_INSIGHT}
          </span>
        </div>
        <div className="chart-container">
          {showChart ? (
            <>
              <div
                id="chartdiv"
                style={{
                  width: "100%",
                  height: "auto", // Allow height to adjust
                  minHeight: "800px", // Maintain minimum height
                }}
              ></div>
              {custom_legends && (
                <CustomLegends customLegends={custom_legends} />
              )}
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">
                <img src={emptyStateIcon} alt="empty-state-icon" />
              </div>
              <div className="empty-state-text">
                Your graph is waiting to take shape!🎨
              </div>
              <div className="empty-state-text">
                Start by selecting output files from Waves and apply them to
                view the graph.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LongitudinalChart;
